import Vue from 'vue';
import axios from 'axios';
import jwt from 'jsonwebtoken';
import { v4 as uuidv4 } from 'uuid';
import appConfig from '@/config';
import store from '@/store';
import { doRefreshToken } from '@/modules/authentication/helpers/refreshToken';
import * as AuthService from '@/modules/authentication/services/authenticationService';

/**
 * Interceptors are executed in order "last in, first out"
 */
export function attachAxiosInterceptors(): void {
  // Set axios max timeout
  axios.defaults.timeout = appConfig.timeoutMs;

  /**
   * Set Authorization header
   */
  axios.interceptors.request.use(
    async (config) => {
      const jwtToken = store.state.authentication.jwtToken ?? null;
      if (jwtToken) {
        config.headers.Authorization = `Bearer ${jwtToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  /**
   * Silent refresh token
   */
  axios.interceptors.request.use(
    async (axiosConfig) => {
      // Don't intercept requests to non-private api
      if (axiosConfig.url?.indexOf(`${appConfig.api.url}${appConfig.api.privateEndpointsPrefix}`) === -1) {
        return axiosConfig;
      }
      // Logout user when refresh token expires
      if (!Vue.$cookies.isKey(appConfig.security.jwtRefreshCookie)) {
        Vue.$cookies.remove(appConfig.security.jwtRefreshCookie);
        window.location.href = AuthService.buildLogoutLink();
      }
      const jwtToken = store.state.authentication.jwtToken ?? '';
      const decoded = jwt.decode(jwtToken);
      // Refresh token if it's about to expire
      if (decoded instanceof Object && decoded.exp) {
        const remainingTime = decoded.exp - new Date().getTime() / 1000;
        if (
          Vue.$cookies.isKey(appConfig.security.jwtRefreshCookie) &&
          remainingTime < appConfig.security.jwtRefreshThreshold
        ) {
          await doRefreshToken();
        }
      }
      return axiosConfig;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  /**
   * Add correlation id to every request
   */
  axios.interceptors.request.use(
    async (config) => {
      // Don't intercept requests to non-private api
      if (config.url?.indexOf(`${appConfig.api.url}`) !== -1) {
        config.headers.CorrelationId = uuidv4();
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}
